/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import type { RefObject } from "react"
import {
	IconArrowUpFill18 as ArrowUp,
	IconPaperPlane4Fill18 as PaperPlane,
} from "nucleo-ui-fill-18"
import { IconBoltOutline18 as SnippetsIcon } from "nucleo-ui-outline-18"

import type { EmailContact } from "@productlane/api"
import { cn } from "@productlane/lib/src/cn"
import { FileAttachments, IconButton, Tooltip } from "@productlane/ui"

import type { ReplyType } from "./types"
import type { Attachment } from "@/screens/app/threads/hooks/useEditorState"
import type { SnippetInput } from "@/screens/settings/settings-pages/snippets-settings-page"
import type { Editor } from "@tiptap/react"
import HelpArticleSuggestions from "@/components/editor/help-articles/help-article-suggestions"
import SnippetSuggestions from "@/components/editor/snippets/snippet-suggestions"
import { FileUpload } from "../../file-upload"
import { AiAssistantDropdown } from "./bottom-bar-ai-assistant"

interface ReplyToolbarProps {
	composeEditor: Editor | null
	replyType: ReplyType
	isForwardMode: boolean
	forwardRecipients: EmailContact[]
	isSendButtonDisabled: boolean
	attachments: Attachment[]
	membershipRole: string | undefined
	containerRef: RefObject<HTMLDivElement | null>
	snippetAnchorRef?: RefObject<HTMLElement | null>
	snippetSuggestionsRef: RefObject<{
		openSnippets: () => void
	} | null>
	writeSnippet: (input: SnippetInput) => null | undefined
	helpArticlesAnchorRef?: RefObject<HTMLElement | null>
	helpArticlesSuggestionsRef: RefObject<{
		openHelpArticles: () => void
	} | null>
	writeHelpArticle: (input: {
		title: string
		linkUrl: string
	}) => null | undefined
	addAttachments: (files: FileList | File[]) => Promise<void>
	removeAttachment: (id: string) => Promise<void>
	contactId: string | null | undefined
	slackConnectSettings: unknown
	send: () => Promise<void>
	handleReplyDisabledClick: () => void
}

export function ReplyToolbar({
	composeEditor,
	replyType,
	isForwardMode,
	forwardRecipients,
	isSendButtonDisabled,
	attachments,
	membershipRole,
	containerRef,
	snippetAnchorRef,
	snippetSuggestionsRef,
	writeSnippet,
	helpArticlesAnchorRef,
	helpArticlesSuggestionsRef,
	writeHelpArticle,
	addAttachments,
	removeAttachment,
	contactId,
	slackConnectSettings,
	send,
	handleReplyDisabledClick,
}: ReplyToolbarProps) {
	return (
		<div>
			{attachments.length > 0 && (
				<div className="mb-2 flex">
					<FileAttachments
						enableViewer
						attachments={attachments}
						onRemove={async (id) => {
							await removeAttachment(id)
						}}
						className="mt-2"
					/>
				</div>
			)}
			<div
				className="flex items-end gap-2"
				ref={containerRef}
				onClick={(e) => {
					if (e.target !== e.currentTarget) return
					composeEditor?.commands.focus("end", {
						scrollIntoView: false,
					})
				}}
			>
				{membershipRole !== "VIEWER" && (
					<div className="flex items-center gap-0.5">
						<AiAssistantDropdown />
						<FileUpload addFiles={addAttachments} />
						{composeEditor && (
							<HelpArticleSuggestions
								ref={helpArticlesSuggestionsRef}
								editor={composeEditor}
								command={writeHelpArticle}
								withoutPortal
								width={containerRef.current?.getBoundingClientRect().width}
								initialOpen={false}
								anchorRef={helpArticlesAnchorRef}
							/>
						)}
						{composeEditor && (
							<SnippetSuggestions
								ref={snippetSuggestionsRef}
								editor={composeEditor}
								command={writeSnippet}
								withoutPortal
								width={containerRef.current?.getBoundingClientRect().width}
								initialOpen={false}
								anchorRef={snippetAnchorRef}
							>
								<IconButton
									Icon={SnippetsIcon}
									variant="ghost"
									className="h-[1.625rem] w-[1.625rem] cursor-default text-secondary hover:text-primary"
									iconClassName="h-4 w-4"
									iconStrokeWidth={1.25}
								/>
							</SnippetSuggestions>
						)}
					</div>
				)}
				<div
					className="flex-1"
					onClick={() => {
						composeEditor?.commands.focus("end", {
							scrollIntoView: true,
						})
					}}
				/>

				<Tooltip
					label={
						membershipRole !== "VIEWER"
							? replyType === "slack"
								? "Send Slack message"
								: replyType === "teams"
									? "Send Teams message"
									: replyType === "email"
										? "Send email"
										: replyType === "live-chat"
											? "Reply"
											: "Send"
							: "Viewers cannot reply"
					}
					side="top"
				>
					<IconButton
						Icon={PaperPlane}
						className={cn(
							"mt-2 cursor-default rounded-full disabled:opacity-100 dark:bg-gray-750 dark:hover:bg-gray-700",
							!isSendButtonDisabled &&
								"dark:bg-gray-200 dark:hover:bg-gray-300",
						)}
						variant={!isSendButtonDisabled ? "solid" : "outline"}
						onClick={() => {
							if (
								isSendButtonDisabled ||
								replyType === "disabled" ||
								(isForwardMode && forwardRecipients.length === 0) ||
								(replyType === "email" && !contactId) ||
								(replyType === "slack" && !slackConnectSettings) ||
								(composeEditor?.isEmpty && !attachments.length) ||
								attachments.some((a) => a.state === "loading")
							) {
								handleReplyDisabledClick()
							} else {
								void send()
							}
						}}
					/>
				</Tooltip>
			</div>
		</div>
	)
}

interface CommentToolbarProps {
	commentEditor: Editor | null
	commentEditorIsEmpty: boolean
	commentAttachments: Attachment[]
	removeCommentAttachment: (id: string) => Promise<void>
	addCommentAttachments: (files: FileList | File[]) => Promise<void>
	send: () => Promise<void>
	handleCommentDisabledClick: () => void
}

export function CommentToolbar({
	commentEditor,
	commentEditorIsEmpty,
	commentAttachments,
	removeCommentAttachment,
	addCommentAttachments,
	send,
	handleCommentDisabledClick,
}: CommentToolbarProps) {
	return (
		<div>
			{commentAttachments.length > 0 && (
				<div className="flex">
					<FileAttachments
						enableViewer
						attachments={commentAttachments}
						onRemove={async (id) => {
							await removeCommentAttachment(id)
						}}
						className="mt-2"
					/>
				</div>
			)}
			<div
				className="flex items-center gap-2"
				onClick={(e) => {
					if (e.target !== e.currentTarget) return
					if (commentEditor) {
						commentEditor.commands.focus("end", {
							scrollIntoView: false,
						})
					}
				}}
			>
				<div
					className="flex-1"
					onClick={() => {
						if (commentEditor) {
							commentEditor.commands.focus("end", {
								scrollIntoView: false,
							})
						}
					}}
				/>
				<FileUpload addFiles={addCommentAttachments} />
				<Tooltip label="Comment" side="top">
					<IconButton
						Icon={ArrowUp}
						className={cn(
							"cursor-default rounded-full disabled:opacity-100",
							commentEditorIsEmpty && !commentAttachments.length
								? "dark:bg-gray-800"
								: "dark:bg-gray-200 dark:hover:bg-gray-300",
						)}
						variant={
							!(commentEditorIsEmpty && !commentAttachments.length)
								? "solid"
								: "outline"
						}
						onClick={() => {
							const isCommentDisabled =
								commentEditor?.isEmpty && !commentAttachments.length
							if (isCommentDisabled) {
								handleCommentDisabledClick()
							} else {
								void send()
							}
						}}
					/>
				</Tooltip>
			</div>
		</div>
	)
}
